import React, { useEffect, useState } from "react";
import {
  fetchStudioCapabilities,
  hasStudioRequirement,
} from "./studioCapabilities";

/**
 * FE role/capability gate for Studio, Advertising, and Ops SPA routes.
 * BE remains authoritative (TpgOpsAuth + controller checks).
 *
 * @param {"studio"|"advertising"|"ops"|"admin"} require
 */
function StudioCapabilityGate({ require = "studio", children }) {
  const [state, setState] = useState({ loading: true, allowed: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const caps = await fetchStudioCapabilities();
      if (!cancelled) {
        setState({
          loading: false,
          allowed: hasStudioRequirement(caps, require),
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [require]);

  if (state.loading) {
    return (
      <div
        className="main-sec-content"
        style={{ padding: "3rem 1.5rem", maxWidth: "32rem" }}
        role="status"
        aria-live="polite"
      >
        <p>Checking access…</p>
      </div>
    );
  }

  if (!state.allowed) {
    // Do not silent-redirect to /home — that collides with the stale-SPA
    // catch-all (/:publicSlug → PublicBrowseGate → /home) and hides whether
    // the route is missing vs capability-denied.
    return (
      <div
        className="main-sec-content"
        style={{ padding: "3rem 1.5rem", maxWidth: "36rem" }}
        role="alert"
      >
        <h1 style={{ fontSize: "1.35rem", marginBottom: "0.75rem" }}>
          Access denied
        </h1>
        <p style={{ marginBottom: "1rem" }}>
          You do not have the <strong>{require}</strong> capability required for
          this page. Platform administrators and authorized studio/ops operators
          can request access from an admin.
        </p>
        <p>
          <a href="/home">Return to Home</a>
        </p>
      </div>
    );
  }

  return children;
}

/**
 * HOC for PrivateRoute `component` prop.
 */
export function withStudioCapability(Component, requirement = "studio") {
  function Gated(props) {
    return (
      <StudioCapabilityGate require={requirement}>
        <Component {...props} />
      </StudioCapabilityGate>
    );
  }
  const name = Component.displayName || Component.name || "Component";
  Gated.displayName = `withStudioCapability(${name})`;
  return Gated;
}

export default StudioCapabilityGate;
